import React, { useCallback, useEffect, useRef, useState } from 'react';
import { brandService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, Btn, DataTable, Field, HardCard, inputClass, shortDate } from '../../components/admin/shell';

const logoUrl = (b) => b.logoUrl || null;
const linkHost = (url) => { try { return new URL(url).host; } catch { return url; } };

/**
 * MARKETING › BRANDS — spec/sections.md §15. The library behind Party Patrons: a table,
 * `+ ADD A BRAND` (create) and EDIT (edit, with the logo upload) in one modal, DELETE (delete)
 * behind a confirm. Absent, not disabled.
 */
export default function BrandsPage() {
  const { can } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const canCreate = can('brands', 'create');
  const canEdit = can('brands', 'edit');
  const canDelete = can('brands', 'delete');
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null); // null | { brand: null } | { brand }

  const load = useCallback(async () => {
    const r = await brandService.list();
    setRows(r.success ? (r.data || []) : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (b) => {
    const ok = await showConfirm({ title: 'DELETE BRAND', message: `Removes ${b.name} from the library. Scheduled patron days for it are cancelled.`, confirmLabel: 'DELETE', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    const r = await brandService.remove(b.id);
    if (!r.success) await showAlert({ title: 'COULD NOT DELETE', message: r.error || 'Try again.', type: 'error', theme: 'admin' });
    load();
  };

  const actions = (b) => [
    canEdit && { label: 'EDIT', onClick: () => setModal({ brand: b }) },
    canDelete && { label: 'DELETE', variant: 'danger', onClick: () => remove(b) },
  ].filter(Boolean);

  const count = (rows || []).length;
  return (
    <>
      <AdminPageHeader
        section="brands"
        meta="The brand library behind Party Patrons."
        actions={canCreate ? <Btn variant="white" onClick={() => setModal({ brand: null })}>+ ADD A BRAND</Btn> : null}
      />
      <DataTable
        columns={[
          { key: 'logo', label: '', fr: 0.5, render: (b) => <Thumb brand={b} /> },
          { key: 'name', label: 'BRAND', fr: 2 },
          { key: 'promoLink', label: 'PROMO', fr: 1.4, render: (b) => (b.promoLink ? linkHost(b.promoLink) : '—') },
          { key: 'patronDays', label: 'PATRON DAYS', fr: 0.9, right: true, render: (b) => (b.patronDays ?? 0).toLocaleString() },
          { key: 'lastDayActive', label: 'LAST RUN', fr: 1, render: (b) => (b.lastDayActive ? shortDate(b.lastDayActive) : 'NEVER') },
          { key: 'createdAt', label: 'ADDED', fr: 1, render: (b) => shortDate(b.createdAt) },
          { key: 'logoPath', label: 'LOGO', fr: 0.8, render: (b) => (b.logoPath ? 'YES' : 'NO') },
        ]}
        rows={rows || []}
        loading={rows == null}
        hasActions={canEdit || canDelete}
        actions={actions}
        empty="NO BRANDS YET."
        footerLeft={`${count} ${count === 1 ? 'brand' : 'brands'}`}
      />
      {modal && (
        <BrandModal
          brand={modal.brand}
          canEdit={canEdit}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

function Thumb({ brand }) {
  const url = logoUrl(brand);
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-[8px] bg-[#F2F2F2] align-middle font-mono text-[10px]">
      {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : brand.logoPath ? 'L' : '◼'}
    </span>
  );
}

function BrandModal({ brand, canEdit, onClose, onSaved }) {
  const isNew = !brand;
  const fileRef = useRef(null);
  const [name, setName] = useState(brand?.name || '');
  const [promoLink, setPromoLink] = useState(brand?.promoLink || '');
  const [message, setMessage] = useState(brand?.defaultMessage || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoNote, setLogoNote] = useState('');

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('A brand needs a name.'); return; }
    setBusy(true); setError('');
    const payload = { name: trimmed, promoLink: promoLink.trim() || undefined, defaultMessage: message.trim() || undefined };
    const r = isNew ? await brandService.create(payload) : await brandService.update(brand.id, payload);
    setBusy(false);
    if (r.success) onSaved(); else setError(r.error || 'Could not save this brand.');
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setLogoNote('');
    const r = await brandService.uploadLogo(brand.id, file);
    setUploading(false);
    setLogoNote(r.success ? 'Logo uploaded.' : (r.error || 'Upload failed.'));
  };

  return (
    <div className="fixed inset-0 safe-inset z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-16" onClick={onClose}>
      <HardCard className="w-full max-w-[560px]" onClick={(e) => e.stopPropagation()}>
        <div onClick={(e) => e.stopPropagation()}>
          <div className="px-6 pb-2 pt-6 font-mono text-[14px] font-bold uppercase tracking-[0.14em]">{isNew ? 'ADD A BRAND' : 'EDIT BRAND'}</div>
          <div className="flex flex-col gap-4 p-4">
            <Field label="BRAND NAME" error={error}><input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} autoFocus /></Field>
            <Field label="PROMO LINK (OPTIONAL)"><input value={promoLink} onChange={(e) => setPromoLink(e.target.value)} className={inputClass} placeholder="https://" /></Field>
            <Field label="DEFAULT MESSAGE (MAX 200)"><textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value.slice(0, 200))} className={inputClass} /></Field>
            {!isNew && canEdit && (
              <Field label="LOGO">
                <div className="flex flex-wrap items-center gap-3">
                  <Btn size="card" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? 'UPLOADING…' : 'UPLOAD LOGO'}</Btn>
                  <span className="font-mono text-[11px] text-[#6B7280]">{logoNote || (brand.logoPath ? 'A logo is on file.' : 'No logo yet.')}</span>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                </div>
              </Field>
            )}
            <div className="flex flex-wrap justify-end gap-2 border-t border-[#F2F2F2] pt-4">
              <Btn onClick={onClose}>CANCEL</Btn>
              <Btn variant="solid" onClick={save} disabled={busy || !name.trim()}>{busy ? 'SAVING…' : isNew ? 'CREATE' : 'SAVE'}</Btn>
            </div>
          </div>
        </div>
      </HardCard>
    </div>
  );
}
